import * as React from "react";

import UploadIcon from "mdi-material-ui/Upload";
import DownloadIcon from "mdi-material-ui/Download";
import EditIcon from "@material-ui/icons/Edit";

// jaza-soft
import { required, translate } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";

import { getDistinctValues, isEmpty, listFromCsv, toFixed, toFixedValue } from "../../../../utils/helpers";
import Tabs from "../../../../components/tab/Tabs";
import SubTab from "../../../../components/tab/SubTab";

import CardHeader from "../../../../components/CardHeader";
import FormDialog from "../../../../components/FormDialog";
import ExcelErrorDialog from "../../../../components/ExcelErrorDialog";
import MyFileInput from "../../../../components/MyFileInput";
import { useDeepMemo, useQuery, useSafeState, useSetting } from "../../../../hooks";

import { colorLightGreen, colorOrange, colorSecondary, colorTomatoRed } from "../../../../utils/colors";
import { CSV, OState, QcStatus, Role, SyncStatus } from "../../../../utils/types";
import hasPrivilege from "../../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../../utils/privilege";

import useRollInventory from "./useRollInventory";
import AllocationDrawer from "./drawers/AllocationDrawer";
import RollRemoveDrawer from "./drawers/RollRemoveDrawer";
import RollSplitDrawer from "./drawers/RollSplitDrawer";
import { OrderFlowType, UOM } from "../../../../constants";
import { convert } from "../../../../helpers/unitConversionHelper";
import SettingKeys from "../../../../utils/SettingKeys";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
            ? colorTomatoRed.dark
            : value === QcStatus.ON_HOLD
              ? colorOrange.dark
              : value === QcStatus.PENDING
                ? colorSecondary.dark
                : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const renderSyncStatus = value => {
  return (
    <span
      style={{
        backgroundColor: [SyncStatus.IN_SYNC, SyncStatus.COMPLETED, SyncStatus.QC_COMPLETED, SyncStatus.PASS].includes(value)
          ? colorLightGreen.dark
          : [SyncStatus.PENDING, SyncStatus.Fail].includes(value)
            ? colorTomatoRed.dark
            : value === SyncStatus.RE_QC
              ? colorOrange.dark
              : "none",
        color: "white",
        padding: "4px 8px",
        borderRadius: 4
      }}>
      {value}
    </span>
  );
};

const footerActions = [
  {
    name: "edit",
    tooltip: "Update",
    icon: <EditIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "download",
    tooltip: "Download",
    icon: <DownloadIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "updateLength",
    tooltip: "Update Stock",
    icon: <UploadIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "updateQc",
    tooltip: "Update QC",
    icon: <UploadIcon />,
    options: { variant: "outlined", color: "primary" }
  }
  // {
  //   name: "allocate",
  //   tooltip: "Allocate by Excel",
  //   icon: <UploadIcon />,
  //   options: { variant: "outlined", color: "primary" }
  // }
];

const RollInventory = props => {
  const { order, ledgerList, selectedRow, effPartName, conversionFactorList, roles, hasAccess, translate, onRefresh } = props;
  const {
    rollFields,
    rollList,
    rows,
    tabIdx,
    dialogState,
    drawerState,
    errorList,
    onValidate,
    onSubmit,
    onDialogSubmit,
    onSelectActionClick,
    onFooterActionClick,
    setTabIdx,
    setDialogState,
    setDrawerState,
    setErrorList
  } = useRollInventory(props);

  const [filterValues, setFilterValues] = useSafeState({});

  const { data: externalApps = [] } = useQuery({ url: "externalApps" });
  const appList = isEmpty(order?.externalIds) ? [] : externalApps.filter(ea => ea?.integrationOperationList?.some(op => "RollInfo" === op.entityId));
  const isKnitwear = rows.some(row => !isEmpty(row.weight));

  const wfxWmsIntegrationEnabled = useSetting(SettingKeys.WFX_WMS_INTEGRATION_ENABLED);
  const allowedCsvValues = useSetting(SettingKeys.ALLOWED_CSV_VALUES) || "";
  const allowedCsvList = listFromCsv(allowedCsvValues) || [];

  const allocatedRollList = useDeepMemo(() => {
    if (isEmpty(rollList)) return [];
    const allocOrderIds = order?.orderFlowList?.filter(of => of.type === OrderFlowType.ALLOCATION)?.map(e => e.id) || [];
    return rollList
      .filter(r => !isEmpty(r.cutOrderId) || !isEmpty(r.groupOrderId) || !isEmpty(r.allocOrderId))
      .filter(r => allocOrderIds.includes(r.allocOrderId))
      .filter(r => r.style === selectedRow?.style && r.color === selectedRow?.color && r.part === effPartName);
  }, [order, rollList, selectedRow, effPartName]);

  const selectActions = useDeepMemo(() => {
    let actions = [
      {
        name: "sync",
        tooltip: "Sync",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "split",
        tooltip: "Split Roll",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "merge",
        tooltip: "Merge Rolls",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 150 } }
      },
      {
        name: "allocate",
        tooltip: "Allocate",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "deallocate",
        tooltip: "Deallocate",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 130 } }
      }
    ];
    if (!isEmpty(allocatedRollList) && tabIdx === 0) {
      //   actions = actions.filter(a => a.name !== "allocate");
    } else {
      actions = actions.filter(a => a.name !== "deallocate");
    }

    //** Syncing QC Details to External System (Manual Button) **//
    if (isEmpty(appList) || wfxWmsIntegrationEnabled !== "false") {
      actions = actions.filter(a => a.name !== "sync");
    }

    return actions;
  }, [allocatedRollList, tabIdx, appList, wfxWmsIntegrationEnabled]);

  const columns = useDeepMemo(() => {
    let columnList = [
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "rollNo", title: "Roll No" },
      {
        dataKey: "qcStatus",
        title: "QC Status",
        align: "center",
        filterOptions: { filter: true },
        inputType: "select-input",
        choices: Object.values(QcStatus).map(v => ({ id: v, name: v })),
        render: renderQcStatus,
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "invStatus",
        title: "Inv. Status",
        align: "center",
        filterOptions: { filter: true }
      },
      {
        dataKey: "rollType",
        title: "Roll Type",
        align: "center",
        filterOptions: { filter: true }
      },
      {
        dataKey: "weight",
        title: "Weight",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 3)} kg`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "length",
        title: "Available\nLength",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "" : `${toFixedValue(value)} m`),
        disabled: row =>
          !isEmpty(row?.weight) || (!isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue)
      },
      {
        dataKey: "finishedWidth",
        title: "Finished\nWidth",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) || value === 0 ? "-" : `${value} cm`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "gsm",
        title: "GSM",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) || value === 0 ? "-" : value),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "width",
        title: "Cuttable\nWidth",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) || value === 0 ? "-" : `${value} cm`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "shade",
        title: "Shade",
        inputType: "text-input",
        render: value => (isEmpty(value) ? "-" : value),
        filterOptions: { filter: true },
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "csv",
        title: "CSV",
        inputType: "select-input",
        choices: Object.values(CSV)
          .filter(e => allowedCsvList.includes(e))
          .map(v => ({ id: v, name: v })),
        filterOptions: { filter: true },
        render: value => (isEmpty(value) ? "-" : value),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "warpShrValue",
        title: "Length \nShrinkage Value",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : value),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "weftShrValue",
        title: "Width \nShrinkage Value",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : value),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "warpShrPercent",
        title: "Length \nShrinkage (%)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value)}%`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "weftShrPercent",
        title: "Width \nShrinkage (%)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value)}%`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "skewnessValue",
        title: "Skewness (%)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value)}%`)
      },
      {
        dataKey: "warpPattern",
        title: "Length \nPattern (%)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value, 1)}%`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "weftPattern",
        title: "Width \nPattern (%)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value, 1)}%`),
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "patternNo",
        title: "Pattern No.",
        inputType: "text-input",
        render: value => value || "-",
        filterOptions: { filter: true },
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "skewness",
        title: "Skewness Group",
        inputType: "text-input",
        render: value => value || "-",
        filterOptions: { filter: true },
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      { dataKey: "invoiceNo", title: "Invoice No", filterOptions: { filter: true } },
      { dataKey: "groupOrder", title: "Group Order", filterOptions: { filter: true } },
      { dataKey: "cutOrder", title: "Cut Order", filterOptions: { filter: true } },
      {
        dataKey: "consignment",
        title: "Consgt. No",
        inputType: "text-input",
        filterOptions: { filter: true },
        render: value => value || "-",
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "rackNo",
        title: "Rack No",
        inputType: "text-input",
        filterOptions: { filter: true },
        render: value => value || "-",
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.FINAL_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "userRemarks",
        title: "Remark",
        inputType: "text-input",
        align: "center",
        disabled: row => !isEmpty(row.groupStateValue) && row.groupStateValue > OState.FINAL_GROUPING_COMPLETED.stateValue
      },
      {
        dataKey: "currWarehouse",
        title: "Current Warehouse",
        align: "center",
        render: value => value || "-",
        filterOptions: { filter: true }
      },
      {
        dataKey: "syncStatus",
        title: "Sync Status",
        align: "center",
        filterOptions: { filter: true },
        render: renderSyncStatus
      },
      {
        dataKey: "returnStatus",
        title: "Return Status",
        align: "center",
        render: value => value || "-",
        filterOptions: { filter: true }
      }
    ];
    if (tabIdx === 0) {
      if (isEmpty(allocatedRollList)) {
        columnList = columnList.filter(c => !["groupOrder", "cutOrder"].includes(c.dataKey));
      } else {
        columnList = columnList.filter(c => !["returnStatus"].includes(c.dataKey));
      }
    } else {
      columnList = columnList.filter(c => !["groupOrder", "cutOrder"].includes(c.dataKey));
    }
    if (!isKnitwear) {
      columnList = columnList.filter(c => !["weight", "gsm", "finishedWidth"].includes(c.dataKey));
    }

    const fields = [...rollFields, "rollType", "invoiceNo", "groupOrder", "cutOrder", "returnStatus", "currWarehouse"];
    columnList = columnList.filter(c => (!isEmpty(appList) && c.dataKey === "syncStatus") || fields.includes(c.dataKey));
    if (roles?.includes(Role.SUPER_USER) || roles?.includes(Role.MASTER)) {
      columnList.push({
        dataKey: "extRemarks",
        title: "Ext Remarks",
        inputType: "text-input",
        align: "center",
        disabled: () => true
      });
    }
    return columnList;
  }, [tabIdx, allocatedRollList, rows, appList, rollFields, isKnitwear, allowedCsvList]);

  const finalFooterActions = hasPrivilege(roles, hasAccess, ResourceId.ROLL, Scope.UPDATE)
    ? footerActions
    : footerActions.filter(a => a.name === "download");

  const consUom = selectedRow?.uom;

  const fileteredRows = rows
    .filter(row => isEmpty(filterValues?.consignment) || filterValues?.consignment === row.consignment)
    .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade === row.shade)
    .filter(row => isEmpty(filterValues?.csv) || filterValues?.csv === row.csv)
    .filter(row => isEmpty(filterValues?.qcStatus) || filterValues?.qcStatus === row.qcStatus);
  const inventoryQty = fileteredRows.reduce((acc, e) => acc + e.length, 0);

  const invQtyInConsUom = useDeepMemo(() => {
    if (consUom === UOM.KILOGRAM.value) {
      return fileteredRows.reduce((acc, row) => acc + row.weight, 0);
    }
    if (isEmpty(consUom)) return null;
    return convert({ input: inventoryQty, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });
  }, [fileteredRows, consUom, conversionFactorList]);

  const title = `Roll Inventory: ${Number(toFixed(inventoryQty, 1))} m${
    consUom !== UOM.METER.value && !isEmpty(invQtyInConsUom) ? ` (${toFixedValue(invQtyInConsUom)} ${UomCodeFromValue[consUom]})` : ""
  }`;

  const titleSelected = selectedRows => {
    const selectedQty = selectedRows.reduce((acc, e) => acc + e.length, 0);
    const selectedQtyInConsUom =
      consUom === UOM.KILOGRAM.value
        ? selectedRows.reduce((acc, e) => acc + e.weight, 0)
        : !isEmpty(consUom)
          ? convert({ input: selectedQty, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList })
          : null;

    return `${selectedRows?.length} Rolls: ${toFixed(selectedQty)} m ${
      consUom !== UOM.METER.value && !isEmpty(selectedQtyInConsUom) ? ` (${toFixed(selectedQtyInConsUom)} ${UomCodeFromValue[consUom]})` : ""
    }  selected`;
  };

  const fabricItemIds = getDistinctValues(ledgerList?.map(e => e.fabricItem?.id).filter(id => !isEmpty(id)));

  console.log({
    allocatedRollList,
    flowIds: order?.orderFlowList?.map(e => e.id),
    allocIds: getDistinctValues(allocatedRollList?.map(e => e.allocOrderId) || [])
  });

  return (
    <div>
      <FormDialog
        maxWidth="sm"
        title={dialogState.title}
        open={dialogState.active && ["updateLength", "updateQc", "allocate"].includes(dialogState.name)}
        onSubmit={onDialogSubmit}
        onClose={() => setDialogState(false)}>
        <MyFileInput
          source="file"
          label={"Excel File"}
          accept={{
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-excel": [".xls"]
          }}
          message={translate("js.message.drop_file", { _: "Drag and drop file here, or click to select file" })}
          xs={12}
          fullWidth
          validate={required()}
        />
      </FormDialog>

      <ExcelErrorDialog maxWidth="xl" open={errorList?.length > 0 || false} errors={errorList} onClose={() => setErrorList(null)} />

      <AllocationDrawer
        open={drawerState.open && drawerState.action === "allocate"}
        order={order}
        flowInfo={selectedRow}
        part={effPartName}
        selectedRolls={drawerState.selectedRolls || []}
        rollFields={rollFields}
        conversionFactorList={conversionFactorList}
        onRefresh={onRefresh}
        onDrawerClose={() => setDrawerState({ open: false, selectedRolls: [], action: null })}
      />

      <RollRemoveDrawer
        open={drawerState.open && drawerState.action === "deallocate"}
        order={order}
        flowInfo={selectedRow}
        part={effPartName}
        selectedRolls={drawerState.selectedRolls || []}
        rollFields={rollFields}
        onRefresh={onRefresh}
        onDrawerClose={() => setDrawerState({ open: false, selectedRolls: [], action: null })}
      />

      <RollSplitDrawer
        open={drawerState.open && drawerState.action === "split"}
        fabricItemIds={fabricItemIds}
        selectedRolls={drawerState.selectedRolls || []}
        rollFields={rollFields}
        onRefresh={onRefresh}
        onDrawerClose={() => setDrawerState({ open: false, selectedRolls: [], action: null })}
      />

      <div style={{ marginTop: "1.5em" }}>
        {allocatedRollList?.length > 0 && (
          <Tabs style={{ marginBottom: "1em" }} value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
            <SubTab label="Allocated Rolls" />
            <SubTab label="Unallocated Rolls" />
          </Tabs>
        )}

        <MuiTable
          key={drawerState.open}
          title={<CardHeader style={{ padding: 0 }} title={title} />}
          titleSelected={titleSelected}
          columns={columns}
          rows={rows}
          pageable
          searchable
          editable={hasPrivilege(roles, hasAccess, ResourceId.ROLL, Scope.UPDATE)}
          selectable={hasPrivilege(roles, hasAccess, ResourceId.ROLL, Scope.UPDATE)}
          pageSelect={false}
          searchKeys={["rollNo", "supplierRollNo"]}
          selectActions={selectActions}
          footerActions={finalFooterActions}
          onSelectActionClick={onSelectActionClick}
          onFooterActionClick={onFooterActionClick}
          validate={onValidate}
          onSubmit={onSubmit}
          onFilter={setFilterValues}
          fontSize={16}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 100]}
          i18nKey="resources.rolls.fields"
        />
      </div>
    </div>
  );
};

export default translate(RollInventory);
